<template>
  <div class="bg-container">
    <div class="main-container">
      <p v-if="selectionMessage" class="text-sm text-gray-500">{{ selectionMessage }}</p>
      <!-- 头部 -->
      <div class="history-header">
        <div class="history-header-left">
          <button class="history-back-btn" @click="goBack">
            <i class="fas fa-arrow-left"></i>
          </button>
          <h1 class="history-header-title">出菜历史</h1>
        </div>
      </div>

      <!-- 筛选控制 -->
      <div class="filter-controls">
        <div class="filter-group">
          <label class="filter-label">下单开始日期</label>
          <input v-model="filters.startDate" type="date" class="filter-input" />
        </div>
        <div class="filter-group">
          <label class="filter-label">下单结束日期</label>
          <input v-model="filters.endDate" type="date" class="filter-input" />
        </div>
        <div class="filter-group">
          <label class="filter-label">餐厅筛选</label>
          <select v-model="filters.shopId" class="filter-input" disabled @change="onShopChange">
            <option value="" disabled>请选择餐厅</option>
            <option v-for="shop in shopList" :key="shop.shopId" :value="String(shop.shopId)">{{
              shop.shopName
            }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">桌号筛选</label>
          <select v-model="filters.tableId" class="filter-input" :disabled="!filters.shopId">
            <option value="">全部桌号</option>
            <option v-for="table in availableTables" :key="table.tableId" :value="table.tableId">{{
              table.tableName
            }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">厨房筛选</label>
          <select
            v-model="filters.kitchenId"
            class="filter-input"
            :disabled="selectionLoading || !kitchens.length"
            @change="onKitchenChange"
          >
            <option value="">全部厨房</option>
            <option
              v-for="kitchen in kitchenList"
              :key="kitchen.kitchenId"
              :value="kitchen.kitchenId"
              >{{ kitchen.kitchenName }}</option
            >
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">灶台筛选</label>
          <select v-model="filters.stoveId" class="filter-input" :disabled="!filters.kitchenId">
            <option value="">全部灶台</option>
            <option v-for="stove in availableStoves" :key="stove.stoveId" :value="stove.stoveId">{{
              stove.stoveName
            }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">菜品名称</label>
          <input
            v-model="filters.dishName"
            type="text"
            class="filter-input"
            placeholder="输入菜品名称"
          />
        </div>
        <div class="filter-group">
          <label class="filter-label">订单号</label>
          <input
            v-model="filters.orderNo"
            type="text"
            class="filter-input"
            placeholder="输入订单号"
          />
        </div>
        <div class="filter-group">
          <label class="filter-label">状态筛选</label>
          <select v-model="filters.status" class="filter-input">
            <option value="">全部</option>
            <option value="2">待出餐</option>
            <option value="8">已出餐</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">打印筛选</label>
          <label class="checkbox-label">
            <input v-model="filters.onlyUnprinted" type="checkbox" class="checkbox-input" />
            仅未打印
          </label>
        </div>
        <div class="filter-group">
          <label class="filter-label">&nbsp;</label>
          <button class="filter-btn" :disabled="loading" @click="loadHistoryList">
            <i class="fas fa-search"></i> {{ loading ? '筛选中...' : '筛选' }}
          </button>
        </div>
        <div class="filter-group">
          <label class="filter-label">&nbsp;</label>
          <button
            class="export-btn"
            :disabled="loading || historyList.length === 0"
            @click="exportToExcel"
          >
            <i class="fas fa-file-excel"></i> 导出Excel
          </button>
        </div>
      </div>

      <!-- 历史记录表格 -->
      <div v-show="historyList.length > 0" class="history-table">
        <div class="table-header">
          出菜历史记录 - 共 <span>{{ historyList.length }}</span> 条记录，菜品总份数
          <span>{{ totalQuantity }}</span> 份
        </div>
        <div class="history-columns">
          <div class="item-info item-columns">
            <div>菜品名称</div>
            <div>桌号信息</div>
            <div>订单号</div>
            <div>下单时间</div>
            <div>出菜时间</div>
            <div>数量</div>
            <div>厨房/灶台</div>
            <div>状态</div>
            <div>打印状态</div>
            <div>用时/人员</div>
          </div>
          <div class="actions actions-column">操作</div>
        </div>
        <div class="history-list">
          <div v-for="item in historyList" :key="item.orderOutId" class="history-item">
            <div class="item-info">
              <div class="dish-info-column">
                <div class="dish-name">{{ item.foodName }}</div>
                <div class="dish-badges">
                  <span
                    v-if="getDishTypeName(item.foodType)"
                    class="dish-type"
                    :class="getDishTypeClass(item.foodType)"
                    >{{ getDishTypeName(item.foodType) }}</span
                  >
                  <!-- 规格显示 -->
                  <span
                    v-if="item.foodSpecName && item.foodSpecName !== '标准'"
                    class="spec-badge"
                    >{{ item.foodSpecName }}</span
                  >
                  <!-- 做法显示 -->
                  <span
                    v-if="item.foodCookDesc && item.foodCookDesc.trim() !== ''"
                    class="cook-badge"
                    ><i class="fas fa-utensils"></i>{{ item.foodCookDesc }}</span
                  >
                </div>
              </div>
              <div class="table-info">{{ item.shopName }}【{{ item.tableName }}】</div>
              <div class="table-info">{{ item.orderNo || '-' }}</div>
              <div class="create-time">
                {{ formatDate(item.createTime) }}<br />
                {{ formatTime(item.createTime) }}
              </div>
              <div class="serve-time">
                {{ formatDate(item.completeTime) }}<br />
                {{ formatTime(item.completeTime) }}
              </div>
              <div class="quantity-info">{{ item.quantity }}份</div>
              <div class="kitchen-stove-info">
                <div>{{ item.kitchenName }}</div>
                <div class="stove-name">{{ item.stoveName }}</div>
              </div>
              <div class="status-info" :class="getStatusClass(item.status)">{{
                item.statusName || '未知'
              }}</div>
              <div
                class="print-status"
                :class="item.isPrint === 0 ? 'print-unprinted' : 'print-printed'"
              >
                {{ item.isPrint === 0 ? '未打印' : `已打印(${item.isPrint ?? '-'})` }}
              </div>
              <div class="time-person-info">
                <div>用时{{ getTimeSpent(item) }}分钟</div>
                <div>{{ item.createBy || '-' }}</div>
              </div>
            </div>
            <div class="actions">
              <button class="action-btn reprint" @click="showReceipt(item)">
                <i class="fas fa-receipt"></i> 补打小票
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-show="historyList.length === 0 && !loading" class="empty-state">
        <i class="fas fa-history"></i>
        <h3>暂无历史记录</h3>
        <p>{{ hasFilters ? '没有符合筛选条件的记录' : '还没有任何出菜记录' }}</p>
      </div>

      <!-- 加载状态 -->
      <div v-show="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>正在加载历史记录...</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, reactive, onMounted, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { fetchTableList, fetchStoveList } from '@/service/base';
  import { getOrderOutSelectList, printFood } from '@/service/order';
  import type { OrderOutSelectListModel } from '@/model/order';
  import { export_json_to_excel } from '@/utils/Export2Excel';

  import { useRestaurantKitchen } from '@/composables/useRestaurantKitchen';

  const router = useRouter();

  const goBack = () => {
    router.back();
  };

  // 数据状态
  const loading = ref(false);
  const {
    shops: shopList,
    currentShop,
    kitchens,
    currentKitchen,
    selectionVersion,
    selectionLoading,
    selectionMessage,
    loadShops,
  } = useRestaurantKitchen();
  const kitchenList = computed(() =>
    kitchens.value.map((item) => ({ kitchenId: item.id, kitchenName: item.name })),
  );
  const tableList = ref<any[]>([]);
  const stoveList = ref<any[]>([]);
  const historyList = ref<OrderOutSelectListModel[]>([]);
  const totalQuantity = computed(() =>
    historyList.value.reduce((total, item) => total + Number(item.quantity || 0), 0),
  );

  // 筛选条件
  const filters = reactive({
    startDate: '', // 下单开始日期
    endDate: '', // 下单结束日期
    shopId: '', // 餐厅ID
    tableId: '', // 桌号ID
    kitchenId: '', // 厨房ID
    stoveId: '', // 灶台ID
    dishName: '', // 菜品名称
    orderNo: '', // 订单号
    status: '', // 状态：空值为全部、2待出餐、8已出餐
    onlyUnprinted: false, // 是否仅查询未打印
  });

  // 计算属性 - 可用桌号列表（根据选择的餐厅筛选）
  const availableTables = computed(() => {
    if (!filters.shopId) {
      return [];
    }
    return tableList.value.filter((table) => table.shopId === Number(filters.shopId));
  });

  // 计算属性 - 可用灶台列表（根据选择的厨房筛选）
  const availableStoves = computed(() => {
    if (!filters.kitchenId) {
      return [];
    }
    return stoveList.value.filter((stove) => stove.kitchenId === Number(filters.kitchenId));
  });

  // 计算属性 - 是否有筛选条件
  const hasFilters = computed(() => {
    return (
      filters.startDate ||
      filters.endDate ||
      filters.shopId ||
      filters.tableId ||
      filters.kitchenId ||
      filters.stoveId ||
      filters.dishName ||
      filters.orderNo ||
      filters.status ||
      filters.onlyUnprinted
    );
  });

  // 餐厅变化处理
  const onShopChange = () => {
    currentShop.value = String(filters.shopId);
  };
  const onKitchenChange = async () => {
    selectionVersion.value++;
    filters.stoveId = '';
    stoveList.value = [];
    if (filters.kitchenId) await loadStoveList(Number(filters.kitchenId));
  };
  watch(
    currentShop,
    (value) => {
      filters.shopId = value;
      filters.tableId = '';
      filters.kitchenId = '';
      filters.stoveId = '';
      tableList.value = [];
      stoveList.value = [];
      historyList.value = [];
      if (value) void loadTableList(Number(value));
    },
    { flush: 'sync' },
  );
  watch(
    selectionVersion,
    () => {
      historyList.value = [];
      loading.value = false;
    },
    { flush: 'sync' },
  );
  watch(currentKitchen, (value) => {
    if (value) void loadHistoryList();
  });

  // 加载桌号列表
  let tableRequest = 0;
  const loadTableList = async (shopId?: number) => {
    if (!shopId) return;
    const shop = currentShop.value;
    const request = ++tableRequest;
    try {
      const params: any = {
        pageNum: 1,
        pageSize: 999,
        data: {},
      };

      if (shopId) {
        params.data.shopId = shopId;
      }

      const response = await fetchTableList(params);

      if (shop !== currentShop.value || request !== tableRequest) return;
      if (response.code !== 200) throw new Error(response.msg || '加载失败');
      if (response.code === 200 && response.result && response.result.list) {
        tableList.value = response.result.list;
      }
    } catch (error) {
      if (shop !== currentShop.value || request !== tableRequest) return;
      showToast('加载选项失败，请重试');
      console.error('加载桌号列表失败:', error);
    }
  };

  // 加载灶台列表
  let stoveRequest = 0;
  const loadStoveList = async (kitchenId?: number) => {
    if (!kitchenId) return;
    const shop = currentShop.value;
    const request = ++stoveRequest;
    try {
      const params: any = {
        pageNum: 1,
        pageSize: 999,
        data: {},
      };

      if (kitchenId) {
        params.data.kitchenId = kitchenId;
      }

      const response = await fetchStoveList(params);

      if (
        shop !== currentShop.value ||
        request !== stoveRequest ||
        String(kitchenId) !== String(filters.kitchenId)
      )
        return;
      if (response.code !== 200) throw new Error(response.msg || '加载失败');
      if (response.code === 200 && response.result && response.result.list) {
        stoveList.value = response.result.list;
      }
    } catch (error) {
      if (
        shop !== currentShop.value ||
        request !== stoveRequest ||
        String(kitchenId) !== String(filters.kitchenId)
      )
        return;
      showToast('加载选项失败，请重试');
      console.error('加载灶台列表失败:', error);
    }
  };

  // 加载出菜历史列表
  let historyRequest = 0;
  const loadHistoryList = async () => {
    if (!currentShop.value || !currentKitchen.value || !kitchens.value.length) return;
    const version = selectionVersion.value;
    const request = ++historyRequest;
    try {
      loading.value = true;

      const params: any = {
        pageNum: 1,
        pageSize: 999,
        data: {},
      };

      // 构建状态筛选条件
      if (filters.status) {
        params.data.statuss = [Number(filters.status)];
      }

      // 构建其他筛选条件
      if (filters.startDate) {
        params.createtimeBegin = filters.startDate + ' 00:00:00';
      }
      if (filters.endDate) {
        params.createtimeEnd = filters.endDate + ' 23:59:59';
      }
      if (filters.shopId) {
        params.data.shopId = Number(filters.shopId);
      }
      if (filters.tableId) {
        params.data.tableId = Number(filters.tableId);
      }
      if (filters.kitchenId) {
        params.data.kitchenId = Number(filters.kitchenId);
      }
      if (filters.stoveId) {
        params.data.stoveId = Number(filters.stoveId);
      }
      if (filters.dishName) {
        params.data.shopFoodName = filters.dishName;
      }
      if (filters.orderNo && String(filters.orderNo).trim()) {
        params.data.orderNo = String(filters.orderNo).trim();
      }
      if (filters.onlyUnprinted) {
        params.data.isPrint = 0;
      }

      const response = await getOrderOutSelectList(params);
      if (version !== selectionVersion.value || request !== historyRequest) return;

      if (response.code !== 200) throw new Error(response.msg || '加载历史失败');
      if (response.code === 200 && response.result) {
        // 处理分页结果，按照其他API的模式
        if (Array.isArray(response.result)) {
          // 如果直接是数组
          historyList.value = response.result;
        } else if (
          response.result &&
          typeof response.result === 'object' &&
          'list' in response.result
        ) {
          // 如果是分页对象，有list字段
          historyList.value = (response.result as any).list || [];
        } else {
          // 如果是单个对象，包装成数组
          historyList.value = [response.result as OrderOutSelectListModel];
        }
      } else {
        historyList.value = [];
      }
      historyList.value = historyList.value.filter(
        (item) =>
          Number(item.shopId) === Number(currentShop.value) &&
          kitchens.value.some((kitchen) => Number(kitchen.id) === Number(item.kitchenId)),
      );
    } catch (error) {
      if (version !== selectionVersion.value || request !== historyRequest) return;
      showToast('加载出菜历史失败，请重试');
      console.error('加载出菜历史失败:', error);
      historyList.value = [];
    } finally {
      if (version === selectionVersion.value && request === historyRequest) loading.value = false;
    }
  };

  // 菜品类型映射
  const getDishTypeName = (foodType: number) => {
    switch (foodType) {
      case 1:
        return '热菜';
      case 2:
        return '冷菜';
      case 3:
        return '汤类';
      case 4:
        return '点心';
      case 5:
        return '饮品';
      default:
        return '';
    }
  };

  const getDishTypeClass = (foodType: number) => {
    switch (foodType) {
      case 1:
        return 'hot';
      case 2:
        return 'cold';
      case 3:
        return 'soup';
      case 4:
        return 'dim-sum';
      case 5:
        return 'drink';
      default:
        return '';
    }
  };

  // 工具函数
  const formatDate = (dateStr: string | Date) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString();
  };

  const formatTime = (dateStr: string | Date) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString();
  };

  const formatDateTime = (dateStr: string | Date) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString();
  };

  const getTimeSpent = (item: OrderOutSelectListModel) => {
    if (!item.startTime || !item.completeTime) return 0;
    const start = new Date(item.startTime);
    const complete = new Date(item.completeTime);
    return Math.floor((complete.getTime() - start.getTime()) / 60000);
  };

  // 获取状态样式类
  const getStatusClass = (status: number | undefined) => {
    switch (status) {
      case 2:
        return 'status-pending';
      case 8:
        return 'status-completed';
      default:
        return '';
    }
  };

  // 补打小票功能
  const showReceipt = async (item: OrderOutSelectListModel) => {
    if (!item.orderOutId) {
      showToast('订单信息不完整，无法补打小票');
      return;
    }

    try {
      const response = await printFood({ orderOutId: item.orderOutId });
      if (response.code === 200) {
        showToast('补打小票成功');
      } else {
        showToast(response.msg || '补打小票失败');
      }
    } catch (error) {
      console.error('补打小票失败:', error);
      showToast('补打小票失败，请稍后重试');
    }
  };

  const showToast = (message: string) => {
    // 创建提示框
    const toast = document.createElement('div');
    toast.className = 'toast-message';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 80px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      z-index: 1000;
      font-size: 16px;
      font-weight: 500;
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 2000);
  };

  // Excel导出功能
  const exportToExcel = () => {
    try {
      // 准备导出数据
      const exportData = historyList.value.map((item, index) => [
        index + 1, // 序号
        item.foodName || '', // 菜品名称 // 菜品类型
        `${item.shopName}【${item.tableName}】`, // 桌号信息
        item.orderNo || '', // 订单号
        formatDateTime(item.createTime), // 下单时间
        formatDateTime(item.completeTime), // 出菜时间
        `${item.quantity}份`, // 菜品数量
        `${item.kitchenName || ''} - ${item.stoveName || ''}`, // 制作厨房/灶台
        item.statusName || '未知', // 状态
        `${getTimeSpent(item)}分钟`, // 用时
        item.createBy || '', // 服务人员
      ]);

      // 表头
      const header = [
        '序号',
        '菜品名称',
        '桌号信息',
        '订单号',
        '下单时间',
        '出菜时间',
        '菜品数量',
        '制作厨房/灶台',
        '状态',
        '用时',
        '服务人员',
      ];

      // 生成文件名
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      const filename = `出菜历史记录_${dateStr}_${timeStr}`;

      // 使用Excel导出工具
      export_json_to_excel({
        header,
        data: exportData,
        filename,
        autoWidth: true,
        bookType: 'xlsx',
      });

      console.log('Excel导出成功');
    } catch (error) {
      console.error('Excel导出失败:', error);
      alert('导出失败，请重试');
    }
  };

  // 生命周期
  onMounted(async () => {
    // 设置默认日期为今天
    filters.startDate = new Date().toISOString().split('T')[0];
    filters.endDate = new Date().toISOString().split('T')[0];

    // 加载基础数据
    await loadShops();
  });
</script>

<style scoped>
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  body {
    font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
    color: #1d1d1f !important;
    padding: 20px !important;
    line-height: 1.6 !important;
  }
  .bg-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #eff6ff 0%, #e0e7ff 55%, #ede9fe 100%);
  }
  .main-container {
    width: 100%;
    max-width: none;
    margin: 0;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
  .history-header {
    position: sticky;
    top: 0;
    z-index: 10;
    min-height: 62px;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background: #3b82f6;
    color: #fff;
  }
  .history-header-left {
    display: flex;
    align-items: center;
  }
  .history-back-btn {
    border: none;
    border-radius: 8px;
    background: #2563eb;
    color: #fff;
    padding: 8px 12px;
    margin-right: 16px;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }
  .history-back-btn:hover {
    background: #1d4ed8;
  }
  .history-header-title {
    margin: 0;
    font-size: 24px;
    line-height: 1.2;
    font-weight: 600;
  }
  .nav-buttons {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
  .nav-btn {
    background: #007aff;
    color: white;
    padding: 10px 16px;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 500;
    transition: background 0.2s;
  }
  .nav-btn:hover {
    background: #0056b3;
  }
  .nav-btn.back {
    background: #6c757d;
  }
  .nav-btn.back:hover {
    background: #5a6268;
  }
  .filter-controls {
    background: white;
    padding: 20px;
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
    margin-bottom: 20px;
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
    align-items: center;
    flex-shrink: 0;
  }
  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .filter-label {
    font-size: 14px;
    font-weight: 500;
    color: #6c757d;
  }
  .filter-input {
    padding: 8px 12px;
    border: 2px solid #e1e1e1;
    border-radius: 6px;
    font-size: 14px;
  }
  .checkbox-label {
    height: 40px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #374151;
    user-select: none;
  }
  .checkbox-input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
  .filter-btn {
    background: #007aff;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.2s;
  }
  .filter-btn:hover {
    background: #0056b3;
  }
  .filter-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  .export-btn {
    background: #28a745;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.2s;
  }
  .export-btn:hover {
    background: #218838;
  }
  .export-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  .history-table {
    background: white;
    border-radius: 0;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .table-header {
    background: #f8f9fa;
    padding: 15px 20px;
    border-bottom: 1px solid #e1e1e1;
    font-weight: 600;
    color: #495057;
    flex-shrink: 0;
  }
  .history-list {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }
  .history-columns {
    display: flex;
    align-items: center;
    padding: 10px 15px;
    background: #f8fafc;
    border-bottom: 1px solid #e5e7eb;
    color: #64748b;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
  }
  .history-item {
    display: flex;
    align-items: flex-start;
    padding: 12px 15px;
    border-bottom: 1px solid #f1f1f1;
    transition: background 0.2s;
    min-height: 80px;
  }
  .history-item:hover {
    background: #f8f9fa;
  }
  .history-item:last-child {
    border-bottom: none;
  }
  .item-info {
    flex: 1;
    min-width: 0;
    display: grid;
    grid-template-columns: 180px 150px 160px 140px 140px 80px 120px 100px 100px 80px;
    gap: 8px;
    align-items: center;
    font-size: 13px;
  }
  .item-columns {
    color: #475569;
    font-size: 12px;
    font-weight: 600;
  }
  .item-columns > div {
    text-align: center;
  }
  .dish-info-column {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: center;
    justify-content: center;
  }
  .dish-name {
    font-weight: 600;
    color: #1d1d1f;
    font-size: 14px;
    text-align: center;
  }
  .dish-badges {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
  }
  .dish-type {
    background: #007aff;
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    display: inline-block;
  }
  .dish-type.hot {
    background: #ff9500;
  }
  .dish-type.cold {
    background: #5ac8fa;
  }
  .dish-type.dim-sum {
    background: #34c759;
  }
  .dish-type.drink {
    background: #007aff;
  }
  .dish-type.soup {
    background: #ff6b6b;
  }
  .spec-badge {
    background: #ff9500;
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    display: inline-block;
  }
  .cook-badge {
    background: #007aff;
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }
  .cook-badge i {
    font-size: 10px;
  }
  .table-info {
    font-size: 13px;
    color: #6c757d;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .time-person-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    color: #6c757d;
    font-size: 12px;
    text-align: center;
    overflow-wrap: anywhere;
  }
  .create-time {
    font-size: 12px;
    color: #28a745;
    font-weight: 500;
    line-height: 1.3;
  }
  .serve-time {
    font-size: 12px;
    color: #17a2b8;
    font-weight: 500;
    line-height: 1.3;
  }
  .quantity-info {
    font-size: 13px;
    color: #17a2b8;
    font-weight: 500;
    text-align: center;
  }
  .kitchen-stove-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
    color: #6c757d;
  }
  .kitchen-stove-info .stove-name {
    font-size: 12px;
    color: #999;
  }
  .status-info {
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    padding: 4px 8px;
    border-radius: 4px;
  }
  .status-info.status-pending {
    background-color: #fff3cd;
    color: #856404;
  }
  .status-info.status-completed {
    background-color: #d4edda;
    color: #155724;
  }
  .print-status {
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    padding: 4px 8px;
    border-radius: 4px;
  }
  .print-status.print-unprinted {
    background-color: #fee2e2;
    color: #991b1b;
  }
  .print-status.print-printed {
    background-color: #dbeafe;
    color: #1e40af;
  }
  .actions {
    display: flex;
    gap: 8px;
    flex: 0 0 104px;
    justify-content: center;
    align-items: center;
  }
  .actions-column {
    color: #475569;
    font-size: 12px;
    font-weight: 600;
  }
  .action-btn {
    background: #28a745;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .action-btn:hover {
    background: #218838;
    transform: translateY(-1px);
  }
  .action-btn.reprint {
    background: #17a2b8;
  }
  .action-btn.reprint:hover {
    background: #138496;
  }
  .empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 60px 20px;
    background: white;
    border-radius: 0;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
  .empty-state i {
    font-size: 48px;
    color: #ccc;
    margin-bottom: 16px;
  }
  .loading-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 60px 20px;
    background: white;
    border-radius: 0;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
  .loading-state i {
    font-size: 48px;
    color: #007aff;
    margin-bottom: 16px;
  }
  .modal {
    display: block;
    position: fixed;
    z-index: 1000;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
  .modal[style*='display: none'] {
    display: none !important;
  }
  .modal-content {
    background-color: white;
    margin: 5% auto;
    padding: 0;
    border-radius: 12px;
    width: 400px;
    max-width: 90%;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
  .modal-header {
    background: #007aff;
    color: white;
    padding: 15px 20px;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .close {
    color: white;
    font-size: 24px;
    font-weight: bold;
    cursor: pointer;
  }
  .close:hover {
    opacity: 0.7;
  }
  .receipt {
    padding: 20px;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    line-height: 1.4;
  }
  .receipt-header {
    text-align: center;
    border-bottom: 2px dashed #ccc;
    padding-bottom: 10px;
    margin-bottom: 15px;
  }
  .receipt-title {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 5px;
  }
  .receipt-item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  .receipt-footer {
    border-top: 2px dashed #ccc;
    padding-top: 10px;
    margin-top: 15px;
    text-align: center;
    font-size: 12px;
    color: #666;
  }
  .print-btn {
    background: #28a745;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    margin: 15px 20px 20px;
    width: calc(100% - 40px);
  }
  .print-btn:hover {
    background: #218838;
  }
  @media (max-width: 1400px) and (min-width: 769px) {
    .item-info {
      grid-template-columns:
        minmax(0, 1.5fr) minmax(0, 1.2fr) minmax(0, 1.25fr) minmax(0, 0.9fr)
        minmax(0, 0.9fr) minmax(42px, 0.5fr) minmax(0, 0.9fr) minmax(0, 0.7fr)
        minmax(0, 0.85fr) minmax(0, 0.8fr);
      gap: 6px;
      font-size: 12px;
    }
    .history-columns,
    .history-item {
      padding-right: 8px;
      padding-left: 8px;
    }
    .actions {
      flex-basis: 92px;
    }
    .action-btn {
      padding: 6px 8px;
      font-size: 11px;
    }
    .dish-name {
      font-size: 13px;
    }
    .dish-type {
      font-size: 10px;
      padding: 1px 4px;
    }
  }
  @media (max-width: 768px) {
    .history-columns {
      display: none;
    }
    .filter-controls {
      flex-direction: column;
      align-items: stretch;
    }
    .item-info {
      display: flex;
      flex-direction: column;
      gap: 8px;
      align-items: flex-start;
      padding: 15px 0;
    }
    .history-item {
      flex-direction: column;
    }
    .item-info > div {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .dish-name {
      font-size: 16px;
      margin-bottom: 5px;
    }
    .actions {
      margin-top: 10px;
      justify-content: flex-end;
      width: 100%;
      flex-basis: auto;
    }
  }
</style>
